/**
 * Where somebody stood before a demo, and where they stand now.
 *
 * "Did the demo do anything" is the question the bookings exist to answer
 * and the one the report could not: it counted demos held, attended and
 * chased, and stopped there. A demo that turned a stranger into a paying
 * customer and one that changed nothing looked the same.
 *
 * ## What this can and cannot know
 *
 * The session has fields for it — `outcome` and `closed_as` — and they
 * are empty: 335 of 337 sessions carry no outcome and not one carries a
 * closed_as. Nothing has been filling them in, so nothing can be read out
 * of them.
 *
 * What is real is the client record. `reg_on`, `trial_end` and
 * `last_paid` are dates, so the same rules that say what somebody is
 * today also say what they were on the day of the demo. That gives a
 * true before and after for the thing that matters — account, trial,
 * money — from data nobody had to remember to type.
 *
 * It does not give plan history. If they were already a customer before
 * the demo, whether they were on Starter then and Growth now is not
 * recorded anywhere, and this says so rather than guessing.
 */
import { parseDate } from '../lib/dates.js';

/* Connector rows come through as the clients app writes them, local rows
   through toLedgerRow. Ask for both rather than making callers care. */
const pick = (c, ...keys) => {
  for (const k of keys) {
    const v = c?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return null;
};

/*
 * The rungs, in order. The number is the point of them: after minus
 * before says whether the demo moved anybody, and by how far.
 */
export const RUNGS = [
  { key: 'none', label: 'Not a client', tone: 'gr-slate' },
  { key: 'free', label: 'Registered', tone: 'gr-violet' },
  { key: 'trial', label: 'On trial', tone: 'gr-amber' },
  { key: 'paying', label: 'Paying', tone: 'gr-green' }
];

const RUNG = Object.fromEntries(RUNGS.map((r, i) => [r.key, i]));
export const rungOf = key => RUNG[key] ?? 0;
export const rungLabel = key => (RUNGS.find(r => r.key === key) || RUNGS[0]).label;

/**
 * What this client was on a given day.
 *
 * `when` is a Date, or null for "now". Everything is judged against it,
 * so the same function answers both halves of the comparison.
 */
export function stageAt(client, when) {
  if (!client) return 'none';
  const at = when ? when.getTime() : Date.now();

  const reg = parseDate(pick(client, 'reg_on', 'regOn'));
  /* No account on that day: either they had not registered yet, or there
     is no registration date at all, which is the same as not knowing. */
  if (!reg || reg.getTime() > at) return 'none';

  /* Money is the top rung, and a payment is the only evidence of it that
     carries a date — pay_status says what is true now, not what was true
     in April.
     *
     * The ledger first, because `last_paid` holds only the most recent
     * payment and every new one overwrites it. Asking "was last_paid on or
     * before the demo" of somebody who has paid every month since 2025
     * answers no — their last payment is this month — so a customer of two
     * years read as unpaid on the day, and the demo was credited with
     * converting somebody it had never converted. The ledger keeps every
     * payment with its date, so it can answer what the single field
     * cannot. */
  const pays = Array.isArray(client.pays) ? client.pays : [];
  if (pays.some(p => {
    const d = parseDate(p && p.d);
    return d && d.getTime() <= at;
  })) return 'paying';

  /* Still the fallback: a client carried over from before the ledger
     existed has a last_paid and no payments behind it. */
  const paid = parseDate(pick(client, 'last_paid', 'lastPaid'));
  if (paid && paid.getTime() <= at) return 'paying';

  /* Nothing paid, so today's pay_status only counts for "now". */
  if (!when && /^yes$/i.test(String(pick(client, 'pay_status', 'pay') || ''))) return 'paying';

  const trialEnd = parseDate(pick(client, 'trial_end', 'trialEnd'));
  if (trialEnd && trialEnd.getTime() > at) return 'trial';

  return 'free';
}

/** The client behind a booking: by the link the sync wrote, else by address. */
export function clientOfBooking(rows, booking) {
  if (!booking || !rows?.length) return null;
  const id = booking.client?.id;
  if (id) {
    const hit = rows.find(r => String(r.id) === String(id));
    if (hit) return hit;
  }
  const email = String(booking.invitee?.email || '').trim().toLowerCase();
  if (!email) return null;
  return rows.find(r => String(r.email || '').trim().toLowerCase() === email) || null;
}

/*
 * What somebody said happened, where they said it.
 *
 * Closing a follow-up asks what it closed as, and that answer is better
 * than anything derived: "Plan upgradation" is the one move the dates
 * cannot show. Empty on every session so far — nobody has closed one —
 * so it is an improvement waiting to be used rather than a source to
 * rely on, and the derived answer stands until it arrives.
 */
const SAID = {
  registration: 'Registered',
  plan_upgrade: 'Upgraded their plan',
  /* The chase ended because another demo was booked, not because anybody
     decided anything. The report should say so rather than counting it with
     the refusals. */
  demo_rescheduled: 'Demo rescheduled',
  not_interested: 'Not interested',
  other: 'Closed'
};

/**
 * One booking's before and after.
 *
 * Returns the two rungs, how far apart they are, the plan on each side,
 * and what somebody said the follow-up closed as. `moved` is negative
 * where they have lapsed since, which is worth seeing too.
 *
 * `planBefore` is a snapshot taken when the booking was first matched to
 * the client, so it is blank on everything matched before that started
 * being recorded. Blank means not known, never "no change" — the two
 * read the same on a screen and mean opposite things.
 */
export function demoShift(rows, booking) {
  const client = clientOfBooking(rows, booking);
  const when = parseDate(booking?.start);
  const before = stageAt(client, when);
  const after = stageAt(client, null);

  const s = booking?.session || {};
  const planBefore = String(s.planBefore || '').trim();
  const today = client ? String(pick(client, 'plan') || '') : '';

  /*
   * "After" is now, not a moment we picked.
   *
   * Somebody who sat through a demo registers or upgrades when they are
   * ready, and that can be a week later or three months later. A reading
   * frozen seven days after the meeting answers a different question —
   * "did they move within the week" — and reports an upgrade made on the
   * eighth day as no change, for ever. Read live, the same report run
   * again next month shows what happened in between, which is the whole
   * reason anybody runs it again.
   *
   * The week-later reading is still taken and still carried here. It is
   * not what the page shows: it is what answers "was this demo's doing"
   * on the day somebody asks, without needing the history rebuilt.
   */
  const planAfter = String(s.planAfter || '').trim();

  return {
    client,
    before,
    after,
    moved: rungOf(after) - rungOf(before),
    /* What they are on right now, read fresh every time this is called. */
    plan: today,
    /* And what they were on a week after the demo, where that was caught.
       Carried rather than shown: it is the answer to "was this the demo's
       doing", which nothing asks yet. */
    planAfter,
    planBefore,
    /* Only a change anybody can see. Same plan, or one end unknown, is
       not something to draw an arrow between. */
    planMoved: !!(planBefore && today && planBefore.toLowerCase() !== today.toLowerCase()),
    said: SAID[s.closedAs] || '',
    saidWhy: s.closedReason || ''
  };
}

/**
 * Which way a plan moved.
 *
 * "Is Growth above Starter" is not a question a name can answer, so the
 * caller supplies the ladder: `rank(plan)` gives a comparable number, or
 * null for a plan it does not recognise. Two plans it cannot rank are not
 * "no change" — they are not known, and the two read the same on a screen
 * while meaning opposite things.
 *
 * Returns 1 up, -1 down, 0 for the same plan, null for cannot say.
 */
export function planMove(rank, before, after) {
  if (!before || !after) return null;
  if (before.toLowerCase() === after.toLowerCase()) return 0;

  const a = rank(before);
  const b = rank(after);
  if (a === null || b === null || a === undefined || b === undefined) return null;
  /* Different names that rank the same: the ladder cannot separate them,
     so neither can this. Calling it "no change" would hide a real move. */
  if (a === b) return null;
  return b > a ? 1 : -1;
}

/**
 * The same over a list, grouped by the move that was made.
 *
 * Only demos somebody actually sat through: a no-show that later
 * registered did not register because of the demo, and counting it would
 * flatter every figure here.
 */
export function shiftSummary(rows, bookings, attended = b => b.session?.attendance === 'attended',
  rank = () => null) {
  const out = {
    held: 0,
    /* Every demo somebody sat through, whatever it changed. The tiles
       count the groups; this is the list the question was actually
       about — what each person was before it and what they are now. */
    all: [],
    moved: [],
    same: [],
    lapsed: [],
    /* Already a customer when they sat down: the interesting group, and
       the one this can say least about. */
    already: [],
    /* Where somebody closed the follow-up and said what it closed as. */
    said: [],
    /* Already a customer, and no record of the plan they were on that
       day — so an upgrade among these is invisible, and the block has to
       admit it rather than call them unchanged. */
    unknownPlan: [],

    /* Had an account or not, on the day and now. Four lists rather than
       four counters, so a tile can open the people behind it. */
    reg: { beforeYes: [], beforeNo: [], afterYes: [], afterNo: [], became: [] },

    /* Which way the plan went, among those who had one on the day. */
    plan: { up: [], down: [], same: [], unknown: [] }
  };

  (bookings || []).filter(attended).forEach(b => {
    const s = demoShift(rows, b);
    out.held += 1;
    const row = { booking: b, ...s };
    out.all.push(row);
    /* A plan change is a move even when the rung did not change: Starter
       to Growth is the whole reason somebody sat through a demo, and the
       ladder cannot see it. */
    if (s.moved > 0 || (s.moved === 0 && s.planMoved)) out.moved.push(row);
    else if (s.moved < 0) out.lapsed.push(row);
    else out.same.push(row);
    if (s.before !== 'none') out.already.push(row);
    if (s.said) out.said.push(row);
    if (s.before !== 'none' && !s.planBefore) out.unknownPlan.push(row);

    /*
     * Registered or not, on the day and now.
     *
     * "Registered" here means they had an account, whatever they were
     * paying — free, trial and paying all have one. The rung ladder
     * already draws that line at `none`.
     */
    const was = s.before !== 'none';
    const is = s.after !== 'none';
    out.reg[was ? 'beforeYes' : 'beforeNo'].push(row);
    out.reg[is ? 'afterYes' : 'afterNo'].push(row);
    if (!was && is) out.reg.became.push(row);

    /*
     * Which way the plan went, for the people who had one to begin with.
     *
     * Somebody who was not a client has no plan to move from, so they are
     * not in this count at all — putting them under "no change" would
     * read as "the demo did nothing" when the truth is the opposite.
     */
    if (was) {
      const dir = planMove(rank, s.planBefore, s.plan);
      if (dir === null) out.plan.unknown.push(row);
      else if (dir > 0) out.plan.up.push(row);
      else if (dir < 0) out.plan.down.push(row);
      else out.plan.same.push(row);
    }
  });

  const byRung = k => out.moved.filter(r => r.after === k && r.moved > 0).length;
  out.became = {
    paying: byRung('paying'),
    trial: byRung('trial'),
    free: byRung('free'),
    upgraded: out.moved.filter(r => r.planMoved).length
  };
  return out;
}
